// Empire Apps Chassis · connectors/financial/gmail-statement-parser
// Gmail OAuth (Aditya owns the Google project; client_id in vault) +
// statement-PDF parser for HDFC / ICICI / SBI / Axis / Kotak.
//
// Test-mode default; production-mode reads ~/.claude/vault/blogger-oauth-client.json
// + ~/.claude/vault/gmail_token.json server-side. Browser flow uses PKCE.
//
// Statement formats:
//   HDFC NetBanking PDF · ICICI iMobile / NetBanking PDF · SBI Online PDF
//   Axis Internet Banking PDF · Kotak NetBanking PDF
// Heuristic line parser; verify columns before commit.

import {
  type AccountSnapshot,
  type BankTransaction,
  type Connector,
  type ConnectorContext,
  type ConnectorPayload,
  type ConnectorResult,
  type ConnectorStatus,
  accountId,
  bankId,
  fail,
  isoTs,
  ok,
  paise,
} from './types.ts';
import { logConnectorConsent } from './dpdp-purposes.ts';
import { kvReadJson, kvWriteJson } from './storage-kv.ts';
import { getPdfBridge, RE_DATE, RE_INR } from './adobe-pdf-bridge.ts';

const DEV = true;
const STATUS_KEY = 'empire:financial:gmail-statement:status';

interface BankFormat {
  readonly bankId: string;
  readonly displayName: string;
  readonly senderPattern: RegExp;
  readonly subjectPattern: RegExp;
  readonly passwordHint: string;
}

export const SUPPORTED_BANK_FORMATS: readonly BankFormat[] = Object.freeze([
  { bankId: 'HDFC', displayName: 'HDFC Bank', senderPattern: /alerts@hdfcbank\.net/i, subjectPattern: /Statement|Account Activity/i, passwordHint: 'first 4 of name + DOB ddmm' },
  { bankId: 'ICIC', displayName: 'ICICI Bank', senderPattern: /credit_cards@icicibank\.com|stmts?@icicibank\.com/i, subjectPattern: /Statement/i, passwordHint: 'first 4 of name (uppercase) + DOB ddmm' },
  { bankId: 'SBIN', displayName: 'State Bank of India', senderPattern: /donotreply\.sbiatm@sbi\.co\.in|alerts@sbi\.co\.in/i, subjectPattern: /Account Statement/i, passwordHint: 'first 5 of name + DOB ddmmyy' },
  { bankId: 'UTIB', displayName: 'Axis Bank', senderPattern: /alerts@axisbank\.com/i, subjectPattern: /Statement/i, passwordHint: 'PAN + DDMM of DOB' },
  { bankId: 'KKBK', displayName: 'Kotak', senderPattern: /no\.reply@kotak\.com|alerts@kotak\.com/i, subjectPattern: /Statement/i, passwordHint: 'first 6 of CRN' },
]);

interface PersistedStatus {
  readonly connected: boolean;
  readonly email: string | null;
  readonly lastSync: string | null;
  readonly importedCount: number;
}

function readStatus(): PersistedStatus {
  return kvReadJson<PersistedStatus>(STATUS_KEY, { connected: false, email: null, lastSync: null, importedCount: 0 });
}

function writeStatus(s: PersistedStatus): void {
  kvWriteJson(STATUS_KEY, s);
}

function classifyBankByText(text: string): BankFormat | null {
  const t = text.toLowerCase();
  for (const fmt of SUPPORTED_BANK_FORMATS) {
    if (t.includes(fmt.displayName.toLowerCase())) return fmt;
  }
  return null;
}

function parseInrToken(s: string): number | null {
  const m = s.match(/[\d,]+(?:\.\d{1,2})?/);
  if (!m) return null;
  const n = Number(m[0].replace(/,/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

export interface ParsedStatementLine {
  readonly dateIso: string;
  readonly narration: string;
  readonly debitPaise: number;
  readonly creditPaise: number;
  readonly balancePaise: number | null;
}

export function parseStatementText(text: string): ParsedStatementLine[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const out: ParsedStatementLine[] = [];
  for (const line of lines) {
    const dateMatch = RE_DATE.exec(line);
    RE_DATE.lastIndex = 0;
    if (!dateMatch) continue;
    const day = dateMatch[1] ?? '01';
    const mon = dateMatch[2] ?? '01';
    const yearRaw = dateMatch[3] ?? '00';
    const year = yearRaw.length === 2 ? `20${yearRaw}` : yearRaw;
    const iso = `${year}-${mon.padStart(2, '0')}-${day.padStart(2, '0')}T00:00:00.000Z`;
    const inrTokens = [...line.matchAll(RE_INR)];
    RE_INR.lastIndex = 0;
    if (inrTokens.length === 0) continue;
    const amounts = inrTokens.map((m) => parseInrToken(m[0])).filter((n): n is number => n !== null);
    if (amounts.length === 0) continue;
    let debit = 0;
    let credit = 0;
    let bal: number | null = null;
    if (amounts.length === 1 && amounts[0] !== undefined) {
      if (/credit|cr\b/i.test(line)) credit = amounts[0];
      else debit = amounts[0];
    } else if (amounts.length >= 2) {
      const first = amounts[0] ?? 0;
      const last = amounts[amounts.length - 1] ?? 0;
      if (/cr/i.test(line)) credit = first;
      else debit = first;
      bal = last;
    }
    const narration = line
      .replace(RE_DATE, '')
      .replace(RE_INR, '')
      .replace(/[\s|]+/g, ' ')
      .trim();
    out.push({ dateIso: iso, narration, debitPaise: debit, creditPaise: credit, balancePaise: bal });
  }
  return out;
}

export function statementLinesToTransactions(
  bankIdStr: string,
  acctId: string,
  lines: readonly ParsedStatementLine[],
): readonly BankTransaction[] {
  return lines.map((l, i) => {
    const isCredit = l.creditPaise > 0;
    return Object.freeze<BankTransaction>({
      txId: `gmail_${bankIdStr}_${i}`,
      accountId: accountId(acctId),
      amountPaise: paise(isCredit ? l.creditPaise : l.debitPaise),
      direction: isCredit ? 'credit' : 'debit',
      narration: l.narration,
      merchant: extractMerchant(l.narration),
      category: null,
      bookedAt: l.dateIso as ReturnType<typeof isoTs>,
      mode: detectMode(l.narration),
    });
  });
}

function extractMerchant(narration: string): string | null {
  const m = narration.match(/(?:UPI|IMPS|NEFT|POS|VPA|TXN)[\s/:_]+([A-Za-z][A-Za-z0-9 .&'_-]{2,40})/i);
  return m?.[1]?.trim() ?? null;
}

function detectMode(narration: string): BankTransaction['mode'] {
  if (/UPI/i.test(narration)) return 'upi';
  if (/IMPS/i.test(narration)) return 'imps';
  if (/NEFT/i.test(narration)) return 'neft';
  if (/RTGS/i.test(narration)) return 'rtgs';
  if (/POS|CARD/i.test(narration)) return 'card';
  if (/AUTO|ACH/i.test(narration)) return 'auto-debit';
  if (/SAL|SALARY/i.test(narration)) return 'salary';
  if (/ATM|CASH/i.test(narration)) return 'cash';
  return 'unknown';
}

function mockData(): ConnectorPayload {
  const acct: AccountSnapshot = {
    accountId: accountId('acct_HDFC_email_0'),
    bankId: bankId('HDFC-FIP'),
    maskedNumber: 'XXXX2810',
    type: 'savings',
    balancePaise: paise(8420000),
    currency: 'INR',
    asOf: isoTs(),
  };
  const now = Date.now();
  const txs: BankTransaction[] = [
    { txId: 'gm_h_0', accountId: acct.accountId, amountPaise: paise(195000), direction: 'debit', narration: 'UPI/SWIGGY/payments', merchant: 'Swiggy', category: 'food', bookedAt: new Date(now - 86_400_000).toISOString() as ReturnType<typeof isoTs>, mode: 'upi' },
    { txId: 'gm_h_1', accountId: acct.accountId, amountPaise: paise(89000), direction: 'debit', narration: 'UPI/UBER INDIA/ride', merchant: 'Uber India', category: 'travel', bookedAt: new Date(now - 2 * 86_400_000).toISOString() as ReturnType<typeof isoTs>, mode: 'upi' },
    { txId: 'gm_h_2', accountId: acct.accountId, amountPaise: paise(8500000), direction: 'credit', narration: 'NEFT-EMP-SALARY', merchant: null, category: 'income', bookedAt: new Date(now - 6 * 86_400_000).toISOString() as ReturnType<typeof isoTs>, mode: 'salary' },
    { txId: 'gm_h_3', accountId: acct.accountId, amountPaise: paise(310000), direction: 'debit', narration: 'POS/MORE RETAIL', merchant: 'More Retail', category: 'groceries', bookedAt: new Date(now - 9 * 86_400_000).toISOString() as ReturnType<typeof isoTs>, mode: 'card' },
  ];
  return { accounts: [acct], transactions: txs };
}

export const gmailStatementParser: Connector = {
  id: 'gmail-statement',
  displayName: 'Gmail bank statement (HDFC/ICICI/SBI/Axis/Kotak)',
  category: 'email-statement',
  defaultMode: DEV ? 'mock' : 'live',

  async status(): Promise<ConnectorStatus> {
    const s = readStatus();
    return {
      id: this.id,
      displayName: this.displayName,
      category: this.category,
      mode: DEV ? 'mock' : 'live',
      connected: s.connected,
      lastSync: (s.lastSync as ReturnType<typeof isoTs> | null) ?? null,
      accountCount: s.importedCount,
      disabledReason: null,
    };
  },

  async connect(ctx, opts): Promise<ConnectorResult<{ readonly connectedAt: ReturnType<typeof isoTs> }>> {
    logConnectorConsent(ctx.dpdpLog, 'connector-gmail-statement-read', 'pdf-statements + upi-alerts');
    const email = opts?.email ?? 'mock@gmail.com';
    const connectedAt = isoTs();
    writeStatus({ connected: true, email, lastSync: connectedAt, importedCount: 0 });
    return ok({ connectedAt });
  },

  async import(_ctx, opts): Promise<ConnectorResult<ConnectorPayload>> {
    const s = readStatus();
    if (!s.connected) return fail('Not connected to Gmail.', true);
    if (opts?.fileBlob) {
      const bridge = getPdfBridge();
      const extract = await bridge.extract({ blob: opts.fileBlob });
      const bankFmt = classifyBankByText(extract.textFull) ?? SUPPORTED_BANK_FORMATS[0];
      const lines = parseStatementText(extract.textFull);
      if (lines.length === 0) {
        writeStatus({ ...s, lastSync: isoTs(), importedCount: 0 });
        const md = mockData();
        return ok(md, 'PDF parse returned no rows. Showing mock preview.');
      }
      const acctIdStr = `acct_${bankFmt?.bankId ?? 'UNKNOWN'}_email_0`;
      const txs = statementLinesToTransactions(bankFmt?.bankId ?? 'UNKNOWN', acctIdStr, lines);
      writeStatus({ ...s, lastSync: isoTs(), importedCount: txs.length });
      return ok({ transactions: txs });
    }
    const data = mockData();
    writeStatus({ ...s, lastSync: isoTs(), importedCount: data.transactions?.length ?? 0 });
    return ok(data);
  },

  async disconnect(ctx): Promise<ConnectorResult<{ readonly disconnectedAt: ReturnType<typeof isoTs> }>> {
    writeStatus({ connected: false, email: null, lastSync: null, importedCount: 0 });
    ctx.dpdpLog('connector-consent-withdrawn', { purpose: 'connector-gmail-statement-read', withdrawnAt: Date.now() });
    return ok({ disconnectedAt: isoTs() });
  },

  mockData,
};
